// Parallel curves of paths and outlines (CIN-15126, phase 1). Pure; relative imports only.
//
// DECISIONS
// - "Right of travel" is the positive side: `d > 0` moves a path to the RIGHT of its direction of travel. For a
//   counter-clockwise ring (every outline) that is OUTWARD, for a clockwise ring inward.
// - Corners are mitered up to `miterLimit` x |d| and beveled beyond it (two points), so an acute corner never
//   grows a spike. Collapsed segments (shorter than `MIN_SEGMENT`) are dropped before offsetting.
// - `offsetOutline` offsets arcs EXACTLY: every point on a curved edge moves along its radial normal, so the
//   offset arc keeps its center and has radius r +- d. Only the corners between edges use the miter rule. A
//   distance that eats an inward arc completely (r - d <= 0) or folds a reflex corner over its own edge is not
//   repaired: callers offset wall thicknesses (small) and insets, not arbitrary distances.

import { arcOf, tessellateEdge } from './arc.ts';
import { edgesOf } from './outline.ts';
import type { OutlineEdge, OutlineLike, PlanPoint } from './types.ts';

const MIN_SEGMENT = 1e-6;
const PARALLEL = 1e-9;

const unit = (dx: number, dy: number): PlanPoint => {
    const length = Math.hypot(dx, dy);
    return length < MIN_SEGMENT ? { x: 0, y: 0 } : { x: dx / length, y: dy / length };
};

/** Right-hand normal of a unit direction. */
const rightOf = (t: PlanPoint): PlanPoint => ({ x: t.y, y: -t.x });

/** The corner point(s) where two offset lines meet: a miter point, or a bevel when the miter is too long. */
function joint(p: PlanPoint, n1: PlanPoint, n2: PlanPoint, d: number, miterLimit: number): PlanPoint[] {
    const dot = n1.x * n2.x + n1.y * n2.y;
    if (1 + dot > PARALLEL) {
        const k = d / (1 + dot);
        const miter = { x: (n1.x + n2.x) * k, y: (n1.y + n2.y) * k };
        if (Math.hypot(miter.x, miter.y) <= miterLimit * Math.abs(d)) {
            return [{ x: p.x + miter.x, y: p.y + miter.y }];
        }
    }
    const a = { x: p.x + n1.x * d, y: p.y + n1.y * d };
    const b = { x: p.x + n2.x * d, y: p.y + n2.y * d };
    return Math.hypot(a.x - b.x, a.y - b.y) < MIN_SEGMENT ? [a] : [a, b];
}

function dedupe(points: PlanPoint[], closed: boolean): PlanPoint[] {
    const out: PlanPoint[] = [];
    for (const p of points) {
        const last = out[out.length - 1];
        if (!last || Math.hypot(p.x - last.x, p.y - last.y) >= MIN_SEGMENT) out.push(p);
    }
    if (closed && out.length > 1) {
        const first = out[0];
        const last = out[out.length - 1];
        if (Math.hypot(first.x - last.x, first.y - last.y) < MIN_SEGMENT) out.pop();
    }
    return out;
}

/**
 * The path moved `d` mm to the right of its travel direction. A closed path is a ring without a repeated last
 * point; the result is a ring too. An open path keeps its two ends (moved along the end normals).
 */
export function offsetPath(points: PlanPoint[], closed: boolean, d: number, miterLimit = 4): PlanPoint[] {
    const path = dedupe(points, closed);
    if (path.length < 2 || d === 0) return path.map((p) => ({ x: p.x, y: p.y }));
    const count = path.length;
    const segments = closed ? count : count - 1;
    const normals: PlanPoint[] = [];
    for (let i = 0; i < segments; i += 1) {
        const a = path[i];
        const b = path[(i + 1) % count];
        normals.push(rightOf(unit(b.x - a.x, b.y - a.y)));
    }
    const out: PlanPoint[] = [];
    if (!closed) {
        out.push({ x: path[0].x + normals[0].x * d, y: path[0].y + normals[0].y * d });
    }
    const first = closed ? 0 : 1;
    const last = closed ? count : count - 1;
    for (let i = first; i < last; i += 1) {
        const before = normals[(i - 1 + segments) % segments];
        const after = normals[i % segments];
        out.push(...joint(path[i], before, after, d, miterLimit));
    }
    if (!closed) {
        const n = normals[segments - 1];
        out.push({ x: path[count - 1].x + n.x * d, y: path[count - 1].y + n.y * d });
    }
    return out;
}

/** The outline's right-hand (outward for a CCW outline) normal at a point ON the edge. */
function outwardNormalAt(edge: OutlineEdge, point: PlanPoint): PlanPoint {
    const arc = arcOf(edge.a, edge.b, edge.bulge);
    if (!arc) {
        return rightOf(unit(edge.b.x - edge.a.x, edge.b.y - edge.a.y));
    }
    const radial = unit(point.x - arc.center.x, point.y - arc.center.y);
    // A counter-clockwise arc travels with the center on its left, so its right-hand side is away from it.
    return edge.bulge > 0 ? radial : { x: -radial.x, y: -radial.y };
}

/**
 * The outline offset by `d` mm as a ring: outward for `d > 0` on a counter-clockwise outline, inward for
 * `d < 0`. Arcs stay concentric (radius +- d), corners are mitered (see the module header).
 */
export function offsetOutline(o: OutlineLike, d: number, miterLimit = 4, tolMm = 20): PlanPoint[] {
    const edges = edgesOf(o);
    const ring: PlanPoint[] = [];
    edges.forEach((edge, index) => {
        const previous = edges[(index + edges.length - 1) % edges.length];
        // The corner at the start of this edge joins the previous edge's end normal to this edge's start normal.
        ring.push(...joint(edge.a, outwardNormalAt(previous, edge.a), outwardNormalAt(edge, edge.a), d, miterLimit));
        if (edge.bulge) {
            const run = tessellateEdge(edge.a, edge.b, edge.bulge, tolMm);
            for (let i = 1; i < run.length - 1; i += 1) {
                const n = outwardNormalAt(edge, run[i]);
                ring.push({ x: run[i].x + n.x * d, y: run[i].y + n.y * d });
            }
        }
    });
    return dedupe(ring, true);
}
